import { withId, withoutId } from "@utils/domain";
import { contentStateFromString, contentStateToString } from "@utils/richText/converters";
import { Category } from "@domain/lms/category/category";
import { MediaDto, mediaFromDto, mediaToDto } from "@domain/media/dtos";
import { Accreditation } from "./accreditation";
import { Organization } from "./organization";
import { OrganizationDraft } from "./organizationDraft";
import { Track } from "@domain/organization/track/track";

export interface OrganizationDraftDto extends Omit<OrganizationDraft, "banner" | "logo" | "tracks" | "presentation"> {
	bannerId?: string;
	avatarId?: string;
	skillAreas?: Category[];
	tracks?: Omit<Track, "id">[];
	presentation?: string;
}

export interface OrganizationDto extends Omit<
	Organization,
	"lastUpdateDate" | "presentation" | "tracks" | "avatar" | "banner"
> {
	avatar?: MediaDto;
	banner?: MediaDto;
	skillAreas: Category[];
	presentation?: string;
	tracks?: Omit<Track, "id">[];
}

export interface AccreditationDraftDto extends Omit<Accreditation, "id" | "logo" | "description"> {
	logoId: string;
	description: string;
}
export interface AccreditationDto extends Omit<Accreditation, "date" | "description"> {
	date?: string;
	description: string;
}

export function organizationDraftToDto(draft: OrganizationDraft): OrganizationDraftDto {
	return {
		...draft,
		bannerId: draft.banner?.id,
		avatarId: draft.avatar?.id,
		skillAreas: draft.categories,
		phoneNumber: draft.phoneNumber || undefined,
		presentation:
			draft.presentation && draft.presentation.getPlainText() ? contentStateToString(draft.presentation) : undefined,
	};
}

export function organizationFromDto(dto: OrganizationDto): Organization {
	return {
		...dto,
		avatar: dto.avatar ? mediaFromDto(dto.avatar) : undefined,
		banner: dto.banner ? mediaFromDto(dto.banner) : undefined,
		categories: dto.skillAreas,
		tracks: dto.tracks?.map(withId),
		description: dto.description ?? "",
		presentation: dto.presentation ? contentStateFromString(dto.presentation) : undefined,
		countryCode: dto.countryCode ?? undefined,
	};
}

export function organizationToDto(organization: Organization): OrganizationDto {
	const { avatar, banner, categories, presentation } = organization;
	return {
		...organization,
		skillAreas: categories,
		avatar: avatar ? mediaToDto(avatar) : undefined,
		banner: banner ? mediaToDto(banner) : undefined,
		presentation: presentation && presentation.getPlainText() ? contentStateToString(presentation) : undefined,
	};
}

export function accreditationDraftToDto({ logo, ...draft }: Accreditation): AccreditationDraftDto {
	return withoutId({
		...draft,
		logoId: logo.id,
		description: contentStateToString(draft.description),
	});
}

export function accreditationFromDto(dto: AccreditationDto): Accreditation {
	return {
		...dto,
		description: contentStateFromString(dto.description),
		date: dto.date ? new Date(dto.date) : undefined,
	};
}
